/**
 * EmptyToolCard — friendly default for the right-rail `ToolCardDock`
 * when no scene selection drives a layer-family card.
 *
 * Per WP-12 (`docs/results/WP-12-toolcarddock-integration-design-brief.md`)
 * the dock is always present in the TwinShell right rail; when the
 * user has not selected anything, the rail must communicate the
 * empty state explicitly rather than render blank space. The copy
 * mirrors the Google Maps "select a place" empty state and follows
 * the platform empty-state pattern from
 * `geosoft-platform-design-system/docs/UIUX_DETAILED_SURFACE_STUDY.md`.
 *
 * Design rules (BLOCKING per `DESIGN_POLICY.md`):
 *   - Tokens only; no raw `#hex`, `rgba()`, pixel literals.
 *   - Material Symbols rounded only via `<Icon>`; both `info` and
 *     `widgets` are already in `ICON_REGISTRY` (no playbook update).
 *   - 40 px touch min — the empty card has no interactive controls,
 *     but the wrapper still keeps a comfortable density.
 *   - One primary CTA per row (none here; the card is informational).
 *   - Non-color cue: the `info` icon plus the explanatory body copy
 *     communicate state without relying on color alone.
 */

import { Icon, tokens } from '@/design';
import { ToolCardShell } from './ToolCardShell';

export interface EmptyToolCardProps {
  /**
   * Optional override for the copy. Defaults to the
   * "Select a layer to open its tools." line specified in
   * `WP-12-toolcarddock-integration-design-brief.md` §7.
   */
  readonly message?: string;
  readonly 'data-testid'?: string;
}

const DEFAULT_MESSAGE =
  'Select a layer in the scene tree to open its layer-family tools here.';

export function EmptyToolCard({
  message = DEFAULT_MESSAGE,
  'data-testid': testId = 'empty-tool-card',
}: EmptyToolCardProps) {
  // WP-15 — the empty-card shell uses a distinct accessible name from
  // the dock's outer `aria-label="Layer tools"` so axe's
  // `landmark-unique` rule does not flag the nested
  // `role="region"` provided by `ToolCardShell`. The user-facing
  // copy still communicates the empty state on the subtitle row.
  return (
    <ToolCardShell
      icon="info"
      title="No layer selected"
      subtitle="Select a layer in the scene tree"
      data-testid={testId}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: tokens.space[3],
          padding: tokens.space[4],
          textAlign: 'center',
          color: tokens.color.ink.secondary,
          fontFamily: tokens.font.family.ui,
          fontSize: tokens.font.role.body.size,
          lineHeight: tokens.font.role.body.lineHeight,
        }}
      >
        <Icon name="widgets" size={32} decorative />
        <p style={{ margin: 0 }}>{message}</p>
      </div>
    </ToolCardShell>
  );
}
